import { createSelector } from "@reduxjs/toolkit";
import {
  PROPOSAL_OUTLINE_STAGES,
  REQUIREMENT_STATUS,
  REQUIREMENT_VERIFICATION,
} from "constants/proposals";
import { get, orderBy } from "lodash";
import isNullish from "utils/isNullish";

import { buildCoverage, buildRequirementByRefLabel } from "./coverage";

const EMPTY = [];

const sliceState = (state) => state?.proposalOutline;

export const selectOutlineOwnerType = (state) => sliceState(state)?.ownerType;
export const selectOutlineOwnerId = (state) => sliceState(state)?.ownerId;
export const selectOutlineOwnerName = (state) => sliceState(state)?.ownerName;

export const selectOutlineOwner = createSelector(
  [selectOutlineOwnerType, selectOutlineOwnerId, selectOutlineOwnerName],
  (ownerType, ownerId, ownerName) => {
    if (!ownerType || ownerId == null) {
      return null;
    }
    return { ownerType, ownerId, ownerName };
  }
);

export const selectHasProposalOutline = (state) =>
  Boolean(sliceState(state)?.data?.id);

export const selectOutlineId = (state) => sliceState(state)?.data?.id;

export const selectIsEditing = (state) => Boolean(sliceState(state)?.isEditing);

export const selectOutlineData = (state) => sliceState(state)?.data;

export const selectExtractionReusedFrom = (state) =>
  selectOutlineData(state)?.extractionReusedFrom ?? null;

export const selectOutlineDataPayload = createSelector(
  [selectOutlineData],
  (data) => data?.outlineData
);

export const selectSectionRequirementIds = (state) =>
  sliceState(state)?.data?.sectionRequirementIds ?? null;

export const selectDocumentLookup = (state) =>
  sliceState(state)?.documentLookup;

export const selectLookupByDocumentId = (documentId) =>
  createSelector([selectDocumentLookup], (lookup) => {
    return lookup && get(lookup, String(documentId));
  });

export const selectProgressHistory = (state) =>
  sliceState(state)?.progressHistory || [];

export const selectProgressConnectionId = createSelector(
  [selectProgressHistory],
  (history) => {
    const item = history.find((item) => item?.payload?.connectionId);
    return item?.payload?.connectionId;
  }
);

export const selectProgressError = (state) => sliceState(state)?.progressError;

export const selectProgressErrorCode = (state) =>
  sliceState(state)?.progressErrorCode;

// The persisted, user-safe failure message for a failed run (see OutlineFailureClassifier) —
// distinct from progressError, which only exists while the tab watched the run fail live.
export const selectOutlineErrorMessage = createSelector(
  [selectOutlineData],
  (data) => data?.errorMessage
);

// Whether a failed run can be resumed from its stage rather than regenerated from scratch.
export const selectIsRetryable = createSelector([selectOutlineData], (data) =>
  Boolean(data?.retryable)
);

export const selectOutlineCreatedBy = createSelector(
  [selectOutlineData],
  (data) => data?.createdBy
);

export const selectIsLocked = createSelector([selectOutlineData], (data) =>
  Boolean(data?.locked)
);

export const selectIsLockedByAnotherUser = createSelector(
  [selectOutlineData],
  (data) => Boolean(data?.locked && !data?.lockedByCurrentUser)
);

export const selectLockedBy = createSelector(
  [selectOutlineData],
  (data) => data?.lockedBy
);

export const selectOutlineLastModified = (state) =>
  sliceState(state)?.data?.modifiedDate || Date.now();

export const selectOutlineCreatedAt = (state) => {
  const { createdDate } = sliceState(state)?.data || {};
  return createdDate ? new Date(Date.now() - createdDate) : null;
};

export const selectProposalOutlineStatus = createSelector(
  [selectOutlineData],
  (data) => data?.processingStatus
);

export const selectProposalOutlineSections = createSelector(
  [selectOutlineData],
  (data) => data?.outlineData?.sections || EMPTY
);

export const selectAreAllSectionsLinked = createSelector(
  [selectProposalOutlineSections],
  (sections) => {
    const documentIds = new Set();

    let section;
    for (section of sections) {
      const { nextgenDocumentId: documentId } = section;

      // If any Section has no linked doc => it can't have a combined doc.
      if (isNullish(documentId)) return false;

      // So if the Set already has an ID and this section's doc ID
      // ISN'T part of that Set, we can infer it's a different linked doc.
      if (documentIds.size > 0 && !documentIds.has(documentId)) return false;

      documentIds.add(documentId);
    }

    return documentIds.size === 1;
  }
);

export const selectSelectedRowIds = (state) =>
  sliceState(state)?.selectedRowIds || EMPTY;

export const selectSelectedIndex = (state) => sliceState(state)?.selectedIndex;

export const selectOriginalSections = (state) =>
  sliceState(state)?.originalSections;

export const selectClarification = (state) =>
  sliceState(state)?.clarification || null;

export const selectDocumentIds = (state) =>
  sliceState(state)?.documentIds || null;

const EMPTY_CLARIFICATION_ANSWERS = EMPTY;

export const selectClarificationAnswers = (state) =>
  sliceState(state)?.clarificationAnswers || EMPTY_CLARIFICATION_ANSWERS;

export const selectOverallProgress = (state) =>
  sliceState(state)?.overallProgress;
export const selectCurrentPhase = (state) => sliceState(state)?.currentPhase;
export const selectCurrentMessage = (state) =>
  sliceState(state)?.currentMessage;
export const selectDocumentPipeline = (state) =>
  sliceState(state)?.documentPipeline;
export const selectDocumentTotal = (state) => sliceState(state)?.documentTotal;
export const selectRequirementsFound = (state) =>
  sliceState(state)?.requirementsFound;
export const selectRequirementsFeed = (state) =>
  sliceState(state)?.requirementsFeed;
export const selectGenerationStartedAt = (state) =>
  sliceState(state)?.generationStartedAt;
export const selectGenerationFailed = (state) =>
  sliceState(state)?.generationFailed;
export const selectConnectionState = (state) =>
  sliceState(state)?.connectionState;
export const selectDetectedType = (state) => sliceState(state)?.detectedType;
export const selectDetectedConfidence = (state) =>
  sliceState(state)?.detectedConfidence;
export const selectDetectedLabel = (state) => sliceState(state)?.detectedLabel;

export const selectGateState = (state) => sliceState(state)?.gate ?? null;

export const selectStructureProposal = (state) =>
  sliceState(state)?.structureProposal ?? null;

export const selectIsAwaitingTypeReview = createSelector(
  [selectProposalOutlineStatus],
  (processingStatus) =>
    processingStatus === PROPOSAL_OUTLINE_STAGES.AWAITING_TYPE_REVIEW
);

export const selectIsAwaitingReqReview = createSelector(
  [selectProposalOutlineStatus],
  (processingStatus) =>
    processingStatus === PROPOSAL_OUTLINE_STAGES.AWAITING_REQ_REVIEW
);

const selectRequirementsTopLevel = (state) => sliceState(state)?.requirements;

export const selectRequirements = createSelector(
  [selectRequirementsTopLevel],
  (requirements) => {
    const active = (requirements || []).filter(
      (requirement) => requirement?.status !== REQUIREMENT_STATUS.REMOVED
    );
    return orderBy(active, ["orderIndex"], ["asc"]);
  }
);

export const selectCoverage = createSelector(
  [selectOutlineDataPayload, selectRequirements, selectSectionRequirementIds],
  buildCoverage
);

// Derived once here rather than rebuilt by each helper that needs it — buildCoverageRows,
// buildSectionTraceSteps and the row-click handler each used to construct their own copy.
export const selectRequirementByRefLabel = createSelector(
  [selectRequirements],
  buildRequirementByRefLabel
);

const selectOutlineDocumentsTopLevel = (state) =>
  sliceState(state)?.outlineDocuments;

export const selectOutlineDocuments = createSelector(
  [selectOutlineDocumentsTopLevel],
  (outlineDocuments) => outlineDocuments || []
);

export const selectVerificationCounts = createSelector(
  [selectRequirements],
  (requirements) => {
    const counts = {
      verified: 0,
      fuzzyMatch: 0,
      unverified: 0,
      userProvided: 0,
      total: requirements.length,
    };

    requirements.forEach((requirement) => {
      switch (requirement?.verification) {
        case REQUIREMENT_VERIFICATION.VERIFIED:
          counts.verified += 1;
          break;
        case REQUIREMENT_VERIFICATION.FUZZY_MATCH:
          counts.fuzzyMatch += 1;
          break;
        case REQUIREMENT_VERIFICATION.UNVERIFIED:
          counts.unverified += 1;
          break;
        case REQUIREMENT_VERIFICATION.USER_PROVIDED:
          counts.userProvided += 1;
          break;
        default:
          break;
      }
    });

    return counts;
  }
);

const EMPTY_PARAGRAPHS = [];

export const selectDocumentParagraphs = (documentId) => (state) =>
  sliceState(state)?.documentParagraphs?.[documentId] || EMPTY_PARAGRAPHS;

// Distinguishes "the fetch has not finished" from "the document genuinely has no paragraphs", which
// an empty array alone cannot: fetchDocumentParagraphs writes [] even when the request fails, so an
// absent key is the only signal that a fetch is still outstanding.
export const selectHasLoadedDocumentParagraphs = (documentId) => (state) =>
  documentId != null &&
  sliceState(state)?.documentParagraphs?.[documentId] !== undefined;

// The document the Coverage view lists paragraphs for: the outline's primary source when it has
// one, else the first. An outline can cite several documents; only one can be listed at a time.
export const selectPrimaryOutlineDocumentId = createSelector(
  [selectOutlineDocuments],
  (documents) => {
    if (documents.length === 0) {
      return null;
    }
    const primary = documents.find((document) => document.isPrimary);
    return (primary || documents[0])?.nextgenDocumentId ?? null;
  }
);
